import { Injectable } from '@angular/core';
import { TokenService } from '../token/token.service';
import { environment as env } from 'src/environments/environment';
import { WebTypeConstant } from 'src/app/shared/constants/common-constant';
import { AESService, AuthService, HttpService, MerchantService, ThemeService } from '..';
import { BaseResponse } from 'src/app/models/base.interface';
import { getAuthenticateWeb } from 'src/app/models/shared/auth/auth.interface';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class AuthWebService {
    constructor(
        private token: TokenService,
        private authService: AuthService,
        private themeService: ThemeService,
        private httpService: HttpService,
        private router: Router,
        private merchantService: MerchantService
    ) { }

    getAuthWeb(isRefresh: boolean = false, appInit: boolean = false): Promise<any> {
        return new Promise(async (resolve) => {
            let currentDomain = window.location.hostname;

            if (!currentDomain.startsWith("www.") && currentDomain !== "localhost") {
                currentDomain = `www.${currentDomain}`;
            }

            if (appInit == false) {
                const storedDomain = this.token.getLocalToken(env.TokenKeys.Domain);
                const storedWebType = this.token.getLocalToken(env.TokenKeys.WebType);
                const storedFavicon = this.token.getLocalToken(env.TokenKeys.Favicon);
                const storedThemeCss = this.token.getLocalToken(env.TokenKeys.ThemeCssToken) ?? '';
                // const storedMerchantCode = this.token.getLocalToken(env.TokenKeys.MerchantCode);
                // const storedMerchantId = this.token.getLocalToken(env.TokenKeys.MerchantId);

                const isAdmin = storedWebType === WebTypeConstant.ADMIN || storedWebType === WebTypeConstant.ADMINSTAFF;
                const isMerchant = !isAdmin;
                const isSameDomain = storedDomain === currentDomain;

                let storedMerchantCode: string | null = null;
                let storedMerchantId: string | null = null;

                if (isMerchant) {
                    storedMerchantCode = this.token.getLocalToken(env.TokenKeys.MerchantCode);
                    storedMerchantId = this.token.getLocalToken(env.TokenKeys.MerchantId);
                }

                const isValidAdmin = isAdmin && isSameDomain;
                const isValidMerchant = isSameDomain && storedMerchantId && storedMerchantCode;
                const isValidSession = (isValidAdmin || isValidMerchant);

                if (!isRefresh && isValidSession) {
                    this.authService.setWebType(storedWebType);
                    this.themeService.updateTheme(storedThemeCss);
                    this.themeService.updateFavicon(storedFavicon ?? '');
                    resolve(true);
                    return;
                }
            }

            const payload = { domain: currentDomain };

            this.httpService.post('auth', 'authenticateweb', 'getauthenticateweb', payload)
                .subscribe((resp: BaseResponse<getAuthenticateWeb>) => {
                    if (resp && resp.code == 1) {
                        if (this.checkDomainExpiration(resp.data, resolve)) {
                            return;
                        }

                        this.token.removeLocalToken(env.TokenKeys.Domain);
                        this.token.removeLocalToken(env.TokenKeys.Favicon);
                        this.token.removeLocalToken(env.TokenKeys.ThemeCssToken);
                        this.token.removeLocalToken(env.TokenKeys.MerchantCode);
                        this.token.removeLocalToken(env.TokenKeys.MerchantId);

                        this.authService.setWebType(resp.data.webType ?? '');
                        // this.authService.setWebType('AdminStaff');
                        this.token.setLocalToken(env.TokenKeys.Domain, currentDomain);
                        this.token.setLocalToken(env.TokenKeys.Favicon, resp.data.faviconUrl);
                        this.token.setLocalToken(env.TokenKeys.ThemeCssToken, resp.data.fileUrl);
                        this.token.setLocalToken(env.TokenKeys.ThemeCssToken, resp.data.fileUrl);
                        this.token.setLocalToken(env.TokenKeys.WebType, resp.data.webType);

                        const isAdmin = resp.data.webType === WebTypeConstant.ADMIN || resp.data.webType === WebTypeConstant.ADMINSTAFF;

                        if (!isAdmin) {
                            const merchantCode = resp.data.merchantCode ?? '';
                            const merchantId = resp.data.merchantId ? resp.data.merchantId.toString() : '0';

                            this.token.setLocalToken(env.TokenKeys.MerchantCode, merchantCode);
                            this.token.setLocalToken(env.TokenKeys.MerchantId, merchantId);

                            this.merchantService.updateMerchantInfo();
                        }

                        this.themeService.updateTheme(resp.data.fileUrl ?? '');
                        this.themeService.updateFavicon(resp.data.faviconUrl ?? '');

                        /* -- BEGIN -- for testing - enter merchant or agent portal */
                        // const merchantId = '1';
                        // const merchantCode = 'ME000';
                        // const webType = 'Merchant'; // Merchant or AgentUnlimited or MerchantStaff or AgentUnlimitedStaff

                        // const themeCss = `https://devwl000api01storage.blob.core.windows.net/content/${merchantCode.toLowerCase()}/merchant/setting/theme-merchant-${merchantCode}.css`;
                        // this.authService.setWebType(webType);
                        // this.token.setLocalToken(env.TokenKeys.WebType, webType);
                        // this.token.setLocalToken(env.TokenKeys.MerchantCode, merchantCode);
                        // this.token.setLocalToken(env.TokenKeys.MerchantId, merchantId);
                        // this.token.setLocalToken(env.TokenKeys.ThemeCssToken, themeCss);
                        // this.themeService.updateTheme(themeCss);
                        /* -- END -- for testing - enter merchant or agent portal */

                        resolve(resp.data);
                        return;
                    } else {
                        this.token.removeLocalToken(env.TokenKeys.Domain);
                        this.token.removeLocalToken(env.TokenKeys.Favicon);
                        this.token.removeLocalToken(env.TokenKeys.ThemeCssToken);
                        this.token.removeLocalToken(env.TokenKeys.MerchantCode);
                        this.token.removeLocalToken(env.TokenKeys.MerchantId);

                        this.themeService.updateTheme('', true);
                        this.router.navigate(['/not-found']);
                        resolve(false);
                        return;
                    }
                });
        });
    }

    checkDomainExpiration(data: any, resolve: (value: boolean) => void): boolean {
        const now = new Date();

        const isExpired = (dateStr: string): boolean => {
            const date = new Date(dateStr);
            return date < now;
        };

        if (!data.isRunBackup) {
            if (data.domainExpiredDate && isExpired(data.domainExpiredDate)) {
                this.router.navigate(['/domain-expired']);
                resolve(false);
                return true;
            }
        } else {
            if (data.backupDomainExpiredDate && isExpired(data.backupDomainExpiredDate)) {
                this.router.navigate(['/domain-expired']);
                resolve(false);
                return true;
            }

            if (data.backupDomain) {
                window.location.href = `https://${data.backupDomain}`;
            }
        }

        return false; // not expired
    }

    // appInitGetAuthWeb(): Promise<any> {
    //     return new Promise(async (resolve) => {
    //         let currentDomain = window.location.hostname;

    //         if (!currentDomain.startsWith("www.") && currentDomain !== "localhost") {
    //             currentDomain = `www.${currentDomain}`;
    //         }

    //         const payload = { domain: 'www.dev-wl001-ag.com' };

    //         this.httpService.post('auth', 'authenticateweb', 'getauthenticateweb', payload)
    //             .subscribe((resp: BaseResponse<getAuthenticateWeb>) => {
    //                 if (resp && resp.code == 1) {
    //                     if (this.checkDomainExpiration(resp.data, resolve)) {
    //                         return;
    //                     }

    //                     this.token.removeLocalToken(env.TokenKeys.Domain);
    //                     this.token.removeLocalToken(env.TokenKeys.Favicon);
    //                     this.token.removeLocalToken(env.TokenKeys.ThemeCssToken);
    //                     this.token.removeLocalToken(env.TokenKeys.MerchantCode);
    //                     this.token.removeLocalToken(env.TokenKeys.MerchantId);

    //                     this.authService.setWebType(resp.data.webType ?? '');
    //                     this.token.setLocalToken(env.TokenKeys.Domain, currentDomain);
    //                     this.token.setLocalToken(env.TokenKeys.Favicon, resp.data.faviconUrl);
    //                     this.token.setLocalToken(env.TokenKeys.ThemeCssToken, resp.data.fileUrl);
    //                     this.token.setLocalToken(env.TokenKeys.ThemeCssToken, resp.data.fileUrl);
    //                     this.token.setLocalToken(env.TokenKeys.WebType, resp.data.webType);

    //                     const isAdmin = resp.data.webType === WebTypeConstant.ADMIN || resp.data.webType === WebTypeConstant.ADMINSTAFF;

    //                     if (!isAdmin) {
    //                         const merchantCode = resp.data.merchantCode ?? '';
    //                         const merchantId = resp.data.merchantId ? resp.data.merchantId.toString() : '0';

    //                         this.token.setLocalToken(env.TokenKeys.MerchantCode, merchantCode);
    //                         this.token.setLocalToken(env.TokenKeys.MerchantId, merchantId);

    //                         this.merchantService.updateMerchantInfo();
    //                     }

    //                     this.themeService.updateTheme(resp.data.fileUrl ?? '');
    //                     this.themeService.updateFavicon(resp.data.faviconUrl ?? '');

    //                     resolve(resp.data);
    //                     return;
    //                 } else {
    //                     this.token.removeLocalToken(env.TokenKeys.Domain);
    //                     this.token.removeLocalToken(env.TokenKeys.Favicon);
    //                     this.token.removeLocalToken(env.TokenKeys.ThemeCssToken);
    //                     this.token.removeLocalToken(env.TokenKeys.MerchantCode);
    //                     this.token.removeLocalToken(env.TokenKeys.MerchantId);

    //                     this.themeService.updateTheme('', true);
    //                     this.router.navigate(['/not-found']);
    //                     resolve(false);
    //                     return;
    //                 }
    //             });
    //     });
    // }

}
